import {
  faArrowsRotate,
  faBagShopping,
  faBell,
  faCalendarDays,
  faBoxOpen,
  faCartShopping,
  faChartColumn,
  faChartLine,
  faChevronLeft,
  faCircleCheck,
  faCircleQuestion,
  faExclamationTriangle,
  faFile,
  faGear,
  faHandHoldingUsd,
  faHome,
  faLanguage,
  faMapLocationDot,
  faMoon,
  faMoonOverSun,
  faPrint,
  faReply,
  faShield,
  faStore,
  faSun,
  faTriangleExclamation,
  faTruck,
  faUser,
  faUsers,
  faWallet,
  faWindowMaximize,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { DropdownItem, MenuItem } from './types';

import { APP_CONFIG } from '@config/index';
import { Team } from '@modules/Auth/models/Auth';
import * as Icons from '@shared/components/ui/icons/warehouse.icons';
import { Bell, Box, Plug, Tags, Upload, Webhook } from 'lucide-react';
import LogoutMenuItem from './LogoutMenuItem';

// 🎯 Menú principal - Función wrapper que selecciona el menú según el tipo de tenant
export function getMainMenuItems(teamType?: string): MenuItem[] {
  if (teamType === 'tenant_external') {
    return getMainMenuItemsExternal();
  }
  // Por defecto, usar el menú de 3PL (también incluye tenant)
  return getMainMenuItems3PL();
}

// 🎯 Menú para tenant_3pl (y tenant)
export function getMainMenuItems3PL(): MenuItem[] {
  return [
    {
      id: 'inicio',
      label: 'menu.inicio',
      path: '/home',
      icon: <FontAwesomeIcon icon={faHome} className="!w-4 !h-4" />,
      teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    },
    {
      id: 'tiendas',
      label: 'menu.tiendas',
      path: '/shops',
      icon: <FontAwesomeIcon icon={faStore} className="!w-4 !h-4" />,
      teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
      permissions: ['shops.view'],
    },
    {
      id: 'clientes',
      label: 'menu.clientes',
      path: '/clients',
      icon: <FontAwesomeIcon icon={faUsers} className="!w-4 !h-4" />,
      teamTypes: ['tenant_3pl'],
    },
    {
      id: 'productos',
      label: 'menu.productos',
      icon: <FontAwesomeIcon icon={faBagShopping} className="!w-4 !h-4" />,
      teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
      permissions: ['products.view', 'packs.view'],
      children: [
        {
          id: 'productos-main',
          label: 'menu.productos_main',
          path: '/products',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          permissions: ['products.view'],
        },
        {
          id: 'productos-packs',
          label: 'menu.productos_packs',
          path: '/packs',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          permissions: ['packs.view'],
        },
        {
          id: 'productos-packaging',
          label: 'menu.packaging',
          path: '/packaging',
          icon: <></>,
          teamTypes: ['tenant_3pl'],
        },
      ],
    },
    {
      id: 'wholesale-orders',
      label: 'menu.wholesaleOrders',
      path: '/wholesale-orders',
      icon: <FontAwesomeIcon icon={faBoxOpen} className="!w-4 !h-4" />,
      teamTypes: ['tenant_3pl'],
      permissions: ['wholesale_orders.view'],
    },

    {
      id: 'wholesale-pickups',
      label: 'menu.wholesalePickups',
      path: '/wholesale-pickups',
      icon: <FontAwesomeIcon icon={faBoxOpen} className="!w-4 !h-4" />,
      teamTypes: ['tenant_3pl'],
      permissions: ['wholesale_orders.view'],
    },
    {
      id: 'pedidos',
      label: 'menu.pedidos',
      icon: <FontAwesomeIcon icon={faCartShopping} className="!w-4 !h-4" />,
      teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
      permissions: ['orders.view'],
      children: [
        {
          id: 'pedidos-main',
          label: 'menu.pedidos_main',
          path: '/orders',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          permissions: ['orders.view'],
        },
        {
          id: 'pedidos-enviados',
          label: 'menu.pedidos_enviados',
          title: 'menu.pedidos_enviados',
          path: '/orders-shipped',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          permissions: ['shipped-orders.view'],
        },
        {
          id: 'crm',
          label: 'menu.crm',
          path: '/orders/crm',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          permissions: ['orders-crm.view'],
          requireCRMEnabled: true, // Solo mostrar si CRM está habilitado
        },
        {
          id: 'crm-management-hours',
          label: 'menu.crm_management_hours',
          path: '/crm/management-hours',
          icon: <></>,
          teamTypes: ['tenant_3pl'],
          permissions: ['orders-crm.view'],
          requireCRMEnabled: true,
        },
        {
          id: 'incidents-main',
          label: 'menu.incidencias_main',
          path: '/incidents',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          // permissions: ['incidents.view'], // Comentado temporalmente
        },
        {
          id: 'shipment-status-transitions',
          label: 'menu.shipment_status_transitions',
          title: 'menu.shipment_status_transitions',
          path: '/orders/shipment-status-transitions',
          icon: <></>,
          teamTypes: ['tenant_3pl'],
          permissions: ['orders.shipment-status-transition.manage'],
        },
      ],
    },
    {
      id: 'reportes',
      label: 'menu.reportes',
      icon: <FontAwesomeIcon icon={faChartLine} className="!w-4 !h-4" />,
      teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
      permissions: [
        'reports.view',
        'stock-movements.view',
        'product-without-stock-report.view',
        'logistics-report.view',
        'inventory-report.view',
      ],
      children: [
        {
          id: 'client-activity',
          label: 'menu.client_activity',
          path: '/reports/client-activity',
          title: 'menu.client_activity',
          icon: <></>,
          teamTypes: ['tenant_3pl'],
          permissions: ['reports.view'],
        },
        {
          id: 'movimientos-stock',
          label: 'menu.titles.stock_movements',
          path: '/stock-movements',
          title: 'menu.titles.stock_movements',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          permissions: ['stock-movements.view'],
        },
        {
          id: 'movimientos-ubicaciones',
          label: 'menu.titles.location_movements',
          path: '/location-movements',
          title: 'menu.titles.location_movements',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl'],
          permissions: ['stock-movements.view'],
        },
        {
          id: 'productos-sin-stock',
          label: 'menu.productos_sin_stock',
          path: '/products-without-stock',
          icon: <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4" />,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          permissions: ['product-without-stock-report.view'],
        },
        {
          id: 'logistics',
          label: 'menu.titles.logistics',
          path: '/reports/logistics',
          title: 'menu.titles.logistics',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          permissions: ['logistics-report.view'],
        },
        {
          id: 'inventory',
          label: 'menu.titles.inventory',
          path: '/reports/inventory',
          title: 'menu.titles.inventory',
          icon: <></>,
          teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
          permissions: ['inventory-report.view'],
        },
        {
          id: 'marketing',
          label: 'menu.titles.marketing',
          path: '/reports/marketing',
          title: 'menu.titles.marketing',
          icon: <></>,
          teamTypes: ['tenant_3pl'],
          permissions: ['logistics-report.view'],
        },
        {
          id: 'cod-payments-reporte',
          label: 'menu.reembolsos_reporte',
          path: '/reports/cod-payments',
          title: 'menu.reembolsos_reporte',
          icon: <></>,
          teamTypes: ['tenant_3pl', 'tenant_external'],
          permissions: ['reports.view'],
        },
      ],
    },
    {
      id: 'rentabilidad',
      label: 'menu.rentabilidad_section',
      icon: <FontAwesomeIcon icon={faHandHoldingUsd} className="!w-4 !h-4" />,
      teamTypes: ['tenant_3pl', 'tenant_external'],
      permissions: ['logistics-report.view'],
      requireProfitabilityAccess: true,
      children: [
        {
          id: 'rentabilidad-resumen',
          label: 'menu.profitability_overview',
          path: '/reports/profitability',
          title: 'menu.titles.profitability',
          icon: <></>,
          teamTypes: ['tenant_3pl', 'tenant_external'],
          permissions: ['logistics-report.view'],
          requireProfitabilityAccess: true,
        },
        {
          id: 'rentabilidad-ad-spend',
          label: 'menu.profitability_ad_spend',
          path: '/reports/profitability/ad-spend',
          title: 'menu.profitability_ad_spend',
          icon: <></>,
          teamTypes: ['tenant_3pl', 'tenant_external'],
          permissions: ['logistics-report.view'],
          requireProfitabilityAccess: true,
        },
        {
          id: 'rentabilidad-product-costs',
          label: 'menu.profitability_product_costs',
          path: '/reports/profitability/product-costs',
          title: 'menu.profitability_product_costs',
          icon: <></>,
          teamTypes: ['tenant_3pl', 'tenant_external'],
          permissions: ['logistics-report.view'],
          requireProfitabilityAccess: true,
        },
      ],
    },
    // 🎯 Centro de ayuda (antes de los grupos Almacén y Contabilidad para tenant_3pl)
    {
      id: 'centro-ayuda',
      label: 'menu.centro_ayuda',
      path: APP_CONFIG.helpCenterUrl,
      icon: <FontAwesomeIcon icon={faCircleQuestion} className="!w-4 !h-4" />,
      teamTypes: ['tenant_3pl'],
      external: true,
    },
    {
      id: 'receptions',
      label: 'menu.receptions',
      path: '/receptions',
      icon: <FontAwesomeIcon icon={faTruck} className="!w-4 !h-4" />,
      teamTypes: ['tenant'],
      permissions: ['receptions.view'],
    },
    {
      id: 'returned',
      label: 'menu.rehusados',
      path: '/reused',
      icon: <Icons.RehusableIcon className="!w-4 !h-4" />,
      teamTypes: ['tenant'],
      permissions: ['returns.view'],
    },
    {
      id: 'refunds',
      label: 'menu.devoluciones',
      path: '/refunds',
      icon: <FontAwesomeIcon icon={faReply} className="!w-4 !h-4" />,
      teamTypes: ['tenant', 'tenant_3pl'],
      permissions: ['returns.view'],
    },
    {
      id: 'identify-order-app',
      label: 'menu.identificar_software',
      path: '/reused/identify-app',
      icon: <Icons.RehusableIcon className="!w-4 !h-4" />,
      teamTypes: ['tenant'],
      permissions: ['returns.view'],
    },
    menuSection(
      'almacen-grupo',
      'menu.almacen_grupo',
      [
        {
          id: 'almacen-dashboard',
          label: 'menu.productivity',
          path: '/warehouse/productivity',
          icon: <FontAwesomeIcon icon={faChartColumn} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['preparations.timings.view', 'returns.view'],
          requireAll: false,
        },
        {
          id: 'almacen-rehusados-dashboard',
          label: 'menu.reusedDashboard',
          path: '/warehouse/dashboard',
          icon: <FontAwesomeIcon icon={faChartColumn} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['returns.view', 'entries.dashboard.view', 'packaging-consumption.dashboard.view'],
          requireAll: false,
        },
        {
          id: 'almacen-configuracion',
          label: 'menu.almacen_configuracion',
          path: '/warehouses',
          icon: <FontAwesomeIcon icon={faStore} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
        },
        {
          id: 'entradas',
          label: 'menu.entradas',
          icon: <FontAwesomeIcon icon={faTruck} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['receptions.view', 'extra-work-orders.view', 'returns.view', 'merchandises.view'],
          children: [
            {
              id: 'entradas-recepciones',
              label: 'menu.entradas_recepciones',
              path: '/receptions',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['receptions.view'],
            },
            {
              id: 'entradas-recepciones-sin-identificar',
              label: 'menu.entradas_recepciones_sin_identificar',
              path: '/unreferenced-entries',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['receptions.view'],
            },
            {
              id: 'entradas-mercancias',
              label: 'menu.entradas_mercancias',
              path: '/merchandise',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['merchandises.view'],
            },
            {
              id: 'entradas-trabajos-extra',
              label: 'menu.entradas_trabajos_extra',
              path: '/extra-work-orders',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['extra-work-orders.view'],
            },
            {
              id: 'entradas-rehusados',
              label: 'menu.entradas_rehusados',
              path: '/reused',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['returns.view'],
            },
            {
              id: 'entradas-recepciones-devoluciones',
              label: 'menu.devoluciones',
              path: '/refund-receptions',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['returns.view'],
            },
            {
              id: 'entradas-identificar-software',
              label: 'menu.entradas_identificar_software',
              path: '/reused/identify-app',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['returns.view'],
            },
          ],
        },
        {
          id: 'cross-docking',
          label: 'menu.cross_docking',
          icon: <FontAwesomeIcon icon={faArrowsRotate} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['cross-docking.operations.view'],
          children: [
            {
              id: 'cross-docking-operations',
              label: 'menu.cross_docking_operations',
              path: '/cross-docking/operations',
              // La recepción de una operación vive bajo /cross-docking/receptions.
              activePaths: ['/cross-docking/receptions'],
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['cross-docking.operations.view'],
            },
          ],
        },
        {
          id: 'preparar-pedidos',
          label: 'menu.preparar_pedidos',
          icon: <FontAwesomeIcon icon={faBoxOpen} className="!w-4 !h-4" />,
          teamTypes: ['tenant', 'tenant_3pl'],
          permissions: [
            'preparations.view',
            'orders.create-preparation',
            'orders.manage-held',
            'preparations.packing',
            'wholesale_orders.view',
          ],
          children: [
            {
              id: 'wholesale-preparations',
              label: 'menu.wholesalePreparations',
              path: '/wholesale-preparations',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['wholesale_orders.view'],
            },
            {
              id: 'pedidos-preparar',
              label: 'menu.pedidos_preparar',
              path: '/pending-orders',
              icon: <></>,
              teamTypes: ['tenant', 'tenant_3pl'],
              permissions: ['orders.create-preparation'],
            },
            {
              id: 'pedidos-retenidos',
              label: 'menu.pedidos_retenidos',
              path: '/held-for-preparation',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['orders.manage-held'],
            },
            {
              id: 'preparar-pedidos-preparaciones',
              label: 'menu.preparaciones',
              path: '/order-preparation',
              icon: <></>,
              teamTypes: ['tenant', 'tenant_3pl'],
              permissions: ['preparations.view'],
            },
            {
              id: 'preparar-pedidos-packing',
              label: 'menu.packing',
              path: '/order-preparation/packing',
              icon: <></>,
              teamTypes: ['tenant', 'tenant_3pl'],
              permissions: ['preparations.packing'],
            },
            {
              id: 'preparar-pedidos-manifests',
              label: 'menu.manifests',
              path: '/order-preparation/manifests',
              icon: <></>,
              teamTypes: ['tenant', 'tenant_3pl'],
              permissions: ['preparations.view'],
            },
          ],
        },
        {
          id: 'almacen-ubicaciones',
          label: 'menu.almacen_ubicaciones',
          icon: <Icons.UbicacionesIcon className="!w-4 !h-4" />,
          teamTypes: ['tenant', 'tenant_3pl'],
          permissions: ['locations.view', 'carts.view', 'wholesale_orders.view', 'merchandise-blocks.manage'],
          children: [
            {
              id: 'almacen-carritos',
              label: 'menu.almacen_carritos',
              path: '/carts',
              icon: <FontAwesomeIcon icon={faCartShopping} className="!w-4 !h-4" />,
              teamTypes: ['tenant', 'tenant_3pl'],
              permissions: ['carts.view'],
            },

            {
              id: 'wholesale-staging-zones',
              label: 'menu.wholesaleStagingZones',
              path: '/locations/staging-zones',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['wholesale_orders.view'],
            },
            {
              id: 'almacen-ubicaciones-listado',
              label: 'menu.almacen_ubicaciones_listado',
              path: '/locations',
              icon: <></>,
              teamTypes: ['tenant', 'tenant_3pl'],
              permissions: ['locations.view'],
            },
            {
              id: 'almacen-ubicaciones-bloqueos',
              label: 'menu.almacen_ubicaciones_bloqueos',
              path: '/merchandise-blocks',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['merchandise-blocks.manage'],
            },
          ],
        },
        {
          id: 'almacen-movimientos-stock',
          label: 'menu.titles.stock_movements',
          path: '/warehouse/stock-movements',
          icon: <FontAwesomeIcon icon={faChartLine} className="!w-4 !h-4" />,
          teamTypes: ['tenant', 'tenant_3pl'],
          permissions: ['stock-movements.view'],
        },
        {
          id: 'almacen-traspasos',
          label: 'menu.almacen_traspasos',
          path: '/stock-transfers/new',
          icon: <FontAwesomeIcon icon={faArrowsRotate} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['stock-transfer.manage'],
        },
        {
          id: 'almacen-reubicar',
          label: 'menu.almacen_reubicar',
          path: '/relocations',
          icon: <FontAwesomeIcon icon={faArrowsRotate} className="!w-4 !h-4" />,
          teamTypes: ['tenant', 'tenant_3pl'],
          permissions: ['carts.view'],
        },
        {
          id: 'almacen-ordenes-ubicacion',
          label: 'menu.almacen_ordenes_ubicacion',
          path: '/location-orders',
          icon: <Icons.OrdenesUbicacionIcon className="!w-4 !h-4" />,
          teamTypes: ['tenant', 'tenant_3pl'],
          permissions: ['location-orders.view'],
        },
        {
          id: 'almacen-lotes',
          label: 'menu.lotes',
          path: '/batches',
          icon: <Box className="!w-4 !h-4" />,
          teamTypes: ['tenant', 'tenant_3pl'],
          permissions: ['products.view'],
        },
        {
          id: 'almacen-puestos-trabajo',
          label: 'menu.puestos_trabajo',
          path: '/packing-stations',
          icon: <FontAwesomeIcon icon={faPrint} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
        },
        {
          id: 'almacen-informes',
          label: 'menu.almacen_informes',
          icon: <FontAwesomeIcon icon={faChartColumn} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['reports.view'],
          children: [
            {
              id: 'almacen-ready-to-prepare-hour',
              label: 'menu.ready_to_prepare_by_hour',
              title: 'menu.ready_to_prepare_by_hour',
              path: '/reports/warehouse/ready-to-prepare-by-hour',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['reports.view'],
            },
            {
              id: 'almacen-warehouse-excel',
              label: 'menu.warehouse_excel_reports',
              title: 'menu.warehouse_excel_reports',
              path: '/reports/warehouse/excel',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['reports.view'],
            },
          ],
        },
      ],
      {
        teamTypes: ['tenant', 'tenant_3pl'],
        permissions: [
          'receptions.view',
          'extra-work-orders.view',
          'returns.view',
          'merchandises.view',
          'preparations.view',
          'orders.create-preparation',
          'orders.manage-held',
          'location-orders.view',
          'locations.view',
          'wholesale_orders.view',
          'carts.view',
          'products.view',
          'reports.view',
        ],
      },
    ),
    menuSection(
      'contabilidad',
      'menu.contabilidad',
      [
        {
          id: 'accounting-dashboard',
          label: 'menu.dashboard',
          path: '/accounting/dashboard',
          icon: <FontAwesomeIcon icon={faChartLine} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: [
            'invoices.view',
            'cod-payments.payouts.view',
            'cod-payments.ledger.view',
            'withdrawals.view',
            'wallets.view',
          ],
        },
        {
          id: 'facturacion',
          label: 'menu.facturacion',
          icon: <Icons.SuscripcionIcon className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          children: [
            {
              id: 'facturacion-pedidos',
              label: 'menu.facturacion_pedidos',
              path: '/invoices/eligible-shipments',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['invoices.view'],
            },
            {
              id: 'facturacion-almacenamiento',
              label: 'menu.facturacion_almacenamiento',
              path: '/invoices/eligible-storage',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['invoices.view'],
            },
            {
              id: 'facturacion-abonos-pedidos',
              label: 'menu.facturacion_abonos_pedidos',
              path: '/invoices/shipment-credits',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['wallet.shipment-credits.view'],
            },
            {
              id: 'facturacion-cuota-minima',
              label: 'menu.facturacion_cuota_minima',
              path: '/invoices/minimum_fees',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['invoices.view'],
            },
            {
              id: 'facturacion-prefacturacion',
              label: 'menu.facturacion_prefacturacion',
              path: '/invoices/pre-invoices',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['invoices.view'],
            },
            {
              id: 'facturacion-facturas',
              label: 'menu.facturacion_facturas',
              path: '/invoices',
              icon: <></>,
              teamTypes: ['tenant_3pl', 'tenant_external'],
              permissions: ['invoices.view'],
            },
            {
              id: 'facturacion-tarifas',
              label: 'menu.facturacion_tarifas',
              path: '/invoices/rates',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['rates.view'],
            },
            {
              id: 'facturacion-trabajos-extra',
              label: 'menu.facturacion_trabajos_extra',
              path: '/invoices/eligible-extra-work',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['invoices.view'],
            },
            {
              id: 'facturacion-wholesale',
              label: 'menu.facturacion_wholesale',
              path: '/invoices/eligible-wholesale-preparation',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['invoices.view'],
            },
            {
              id: 'facturacion-crm-management',
              label: 'menu.facturacion_crm_management',
              path: '/invoices/eligible-crm-management',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['invoices.view'],
            },
          ],
        },
        {
          id: 'cod-payments',
          label: 'menu.cod_payments',
          icon: <FontAwesomeIcon icon={faHandHoldingUsd} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: [
            'cod-payments.payouts.view',
            'cod-payments.payments.view',
            'cod-payments.files.view',
            'cod-payments.ledger.view',
            'cod-payments.reviews.view',
          ],
          children: [
            {
              id: 'cod-payouts',
              label: 'menu.cod_payouts',
              path: '/cod-payments/payouts',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['cod-payments.payouts.view'],
            },
            {
              id: 'cod-courier-files',
              label: 'menu.cod_courier_files',
              path: '/cod-payments/courier-files',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['cod-payments.files.view'],
            },
            {
              id: 'cod-reviews',
              label: 'menu.cod_reviews',
              path: '/cod-payments/reviews',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['cod-payments.reviews.view'],
            },
          ],
        },
        {
          id: 'wallets',
          label: 'menu.billeteras',
          icon: <FontAwesomeIcon icon={faWallet} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['wallets.view'],
          children: [
            {
              id: 'wallets-main',
              label: 'menu.billeteras_main',
              path: '/wallets',
              icon: <></>,
              teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
              permissions: ['wallets.view'],
              requireWalletVisible: true,
            },
            {
              id: 'wallets-bank-accounts',
              label: 'menu.cuentas_bancarias',
              path: '/wallets/bank-accounts',
              icon: <></>,
              teamTypes: ['tenant_3pl', 'tenant_external'],
              permissions: ['bank-accounts.view'],
              requireWalletVisible: true,
            },
            {
              id: 'wallets-withdrawals',
              label: 'menu.retiros',
              path: '/wallets/withdrawals',
              icon: <></>,
              teamTypes: ['tenant_3pl', 'tenant_external'],
              permissions: ['withdrawals.view'],
              requireWalletVisible: true,
            },
            {
              id: 'wallets-payment-files',
              label: 'menu.payment_files',
              path: '/withdrawal-payment-files',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['withdrawals.view'],
              requireWalletVisible: true,
            },
            {
              id: 'wallets-topups',
              label: 'menu.recargas',
              path: '/wallets/topups',
              icon: <></>,
              teamTypes: ['tenant_3pl', 'tenant_external'],
              permissions: ['topups.view'],
              requireWalletVisible: true,
            },
          ],
        },
        {
          id: 'contabilidad-carga-datos',
          label: 'menu.contabilidad_carga_datos',
          icon: <Upload className="!w-4 !h-4" />,
          path: '/accounting/data-load',
          teamTypes: ['tenant_3pl'],
          permissions: ['invoices.view'],
        },
        {
          id: 'reportes-contabilidad',
          label: 'menu.reportes_contabilidad',
          icon: <FontAwesomeIcon icon={faChartLine} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['reports.view'],
          children: [
            {
              id: 'orders-without-weight',
              label: 'menu.orders_without_weight',
              path: '/reports/orders-without-weight',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['reports.view'],
            },
            {
              id: 'reports-3pl-excel',
              label: 'menu.three_pl_excel_reports',
              path: '/reports/3pl/excel',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['reports.view'],
            },
            {
              id: 'reports-legacy-dashboard',
              label: 'menu.legacy_dashboard',
              path: '/reports/3pl/legacy-dashboard',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['reports.view'],
            },
            {
              id: 'reembolsos-reporte',
              label: 'menu.reembolsos_reporte',
              path: '/cod-payments/mismatches',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['cod-payments.ledger.view'],
            },
            {
              id: 'reports-wallet-accounting-sync',
              label: 'menu.wallets_accounting_sync',
              path: '/reports/wallet-accounting-sync',
              icon: <></>,
              teamTypes: ['tenant_3pl'],
              permissions: ['wallets.view'],
              requireHoldedAccounting: true,
            },
          ],
        },
        {
          id: 'centros-coste',
          label: 'menu.centros_coste',
          path: '/analytical-accounting/cost-centers',
          icon: <FontAwesomeIcon icon={faChartColumn} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['analytical-accounting.cost-centers.manage'],
        },
        // Hotfix: afiliados ocultos (ver imports lazy de protectedRoutes).
        // {
        //   id: 'afiliados-partners-grupo',
        //   label: 'menu.afiliadosPartnersGrupo',
        //   icon: <FontAwesomeIcon icon={faHandHoldingUsd} className="!w-4 !h-4" />,
        //   teamTypes: ['tenant_3pl'],
        //   children: [
        //     {
        //       id: 'afiliados-partners',
        //       label: 'menu.afiliadosPartners',
        //       path: '/accounting/affiliate-partners',
        //       icon: <></>,
        //       teamTypes: ['tenant_3pl'],
        //       permissions: ['affiliates.view'],
        //     },
        //     {
        //       id: 'facturas-afiliados-partners',
        //       label: 'menu.facturasAfiliadosPartners',
        //       path: '/accounting/affiliate-commissions',
        //       icon: <></>,
        //       teamTypes: ['tenant_3pl'],
        //       permissions: ['affiliates.commissions.backoffice.view'],
        //     },
        //   ],
        // },
      ],
      {
        teamTypes: ['tenant_3pl'],
        permissions: [
          'invoices.view',
          'cod-payments.payouts.view',
          'cod-payments.payments.view',
          'cod-payments.files.view',
          'cod-payments.ledger.view',
          'wallets.view',
        ],
      },
    ),
    menuSection(
      'logistica',
      'menu.logistica',
      [
        {
          id: 'sla-alertas',
          label: 'menu.sla_alertas',
          path: '/logistics/sla/alerts',
          icon: <FontAwesomeIcon icon={faTriangleExclamation} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['sla.rules.view'],
        },
        {
          id: 'sla-reclamaciones',
          label: 'menu.sla_reclamaciones',
          path: '/logistics/sla/claims',
          icon: <FontAwesomeIcon icon={faBell} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['sla.rules.view'],
        },
        {
          id: 'sla-reglas',
          label: 'menu.sla_reglas',
          path: '/logistics/sla/config/rules',
          icon: <FontAwesomeIcon icon={faTruck} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['sla.rules.view'],
        },
        {
          id: 'sla-falsas-entregas',
          label: 'menu.sla_falsas_entregas',
          path: '/logistics/sla/config/false-delivery',
          icon: <FontAwesomeIcon icon={faTruck} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['sla.rules.view'],
        },
        {
          id: 'sla-tipos-incumplimiento',
          label: 'menu.sla_tipos_incumplimiento',
          path: '/logistics/sla/config/breach-types',
          icon: <FontAwesomeIcon icon={faTruck} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['sla.rules.view'],
        },
        {
          id: 'tracking-fallos-api',
          label: 'menu.trackingFailures',
          path: '/logistics/couriers/tracking-failures',
          icon: <FontAwesomeIcon icon={faExclamationTriangle} className="!w-4 !h-4" />,
          teamTypes: ['tenant_3pl'],
          permissions: ['tracking-monitoring.view'],
        },
      ],
      {
        teamTypes: ['tenant_3pl'],
        // Cualquiera de los dos permisos habilita la sección; cada hijo sigue gateando el suyo propio.
        permissions: ['sla.rules.view', 'tracking-monitoring.view'],
      },
    ),
  ];
}

// 🎯 Menú para tenant_external
export function getMainMenuItemsExternal(): MenuItem[] {
  return [
    {
      id: 'inicio',
      label: 'menu.inicio',
      path: '/home',
      icon: <FontAwesomeIcon icon={faHome} className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
    },
    {
      id: 'tiendas',
      label: 'menu.tiendas',
      path: '/shops',
      icon: <FontAwesomeIcon icon={faStore} className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
      permissions: ['shops.view'],
    },
    {
      id: 'productos',
      label: 'menu.productos',
      icon: <FontAwesomeIcon icon={faBagShopping} className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
      permissions: ['products.view', 'packs.view'],
      children: [
        {
          id: 'productos-main',
          label: 'menu.productos_main',
          path: '/products',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['products.view'],
        },
        {
          id: 'productos-packs',
          label: 'menu.productos_packs',
          path: '/packs',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['packs.view'],
        },
      ],
    },
    // Pedidos mayoristas ocultos temporalmente al cliente durante las pruebas en producción.
    {
      id: 'pedidos',
      label: 'menu.pedidos',
      icon: <FontAwesomeIcon icon={faCartShopping} className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
      permissions: ['orders.view'],
      children: [
        {
          id: 'pedidos-main',
          label: 'menu.pedidos_main',
          path: '/orders',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['orders.view'],
        },
        {
          id: 'pedidos-enviados',
          label: 'menu.pedidos_enviados',
          title: 'menu.pedidos_enviados',
          path: '/orders-shipped',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['shipped-orders.view'],
        },
        {
          id: 'crm',
          label: 'menu.crm',
          path: '/orders/crm',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['orders-crm.view'],
          requireCRMEnabled: true, // Solo mostrar si CRM está habilitado
        },
        {
          id: 'incidents-main',
          label: 'menu.incidencias_main',
          path: '/incidents',
          icon: <></>,
          teamTypes: ['tenant_external'],
          // permissions: ['incidents.view'], // Comentado temporalmente
        },
      ],
    },
    {
      id: 'reportes',
      label: 'menu.reportes',
      icon: <FontAwesomeIcon icon={faChartLine} className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
      permissions: [
        'reports.view',
        'stock-movements.view',
        'product-without-stock-report.view',
        'logistics-report.view',
        'inventory-report.view',
      ],
      children: [
        {
          id: 'movimientos-stock',
          label: 'menu.titles.stock_movements',
          path: '/stock-movements',
          title: 'menu.titles.stock_movements',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['stock-movements.view'],
        },
        {
          id: 'productos-sin-stock',
          label: 'menu.productos_sin_stock',
          path: '/products-without-stock',
          icon: <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4" />,
          teamTypes: ['tenant_external'],
          permissions: ['product-without-stock-report.view'],
        },
        {
          id: 'logistics',
          label: 'menu.titles.logistics',
          path: '/reports/logistics',
          title: 'menu.titles.logistics',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['logistics-report.view'],
        },
        {
          id: 'inventory',
          label: 'menu.titles.inventory',
          path: '/reports/inventory',
          title: 'menu.titles.inventory',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['inventory-report.view'],
        },
        {
          id: 'cod-payments-reporte',
          label: 'menu.reembolsos_reporte',
          path: '/reports/cod-payments',
          title: 'menu.reembolsos_reporte',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['reports.view'],
        },
      ],
    },
    {
      id: 'rentabilidad',
      label: 'menu.rentabilidad_section',
      icon: <FontAwesomeIcon icon={faHandHoldingUsd} className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
      permissions: ['logistics-report.view'],
      requireProfitabilityAccess: true,
      children: [
        {
          id: 'rentabilidad-resumen',
          label: 'menu.profitability_overview',
          path: '/reports/profitability',
          title: 'menu.titles.profitability',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['logistics-report.view'],
          requireProfitabilityAccess: true,
        },
        {
          id: 'rentabilidad-ad-spend',
          label: 'menu.profitability_ad_spend',
          path: '/reports/profitability/ad-spend',
          title: 'menu.profitability_ad_spend',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['logistics-report.view'],
          requireProfitabilityAccess: true,
        },
        {
          id: 'rentabilidad-product-costs',
          label: 'menu.profitability_product_costs',
          path: '/reports/profitability/product-costs',
          title: 'menu.profitability_product_costs',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['logistics-report.view'],
          requireProfitabilityAccess: true,
        },
        {
          id: 'rentabilidad-shipping-costs',
          label: 'menu.profitability_shipping_costs',
          path: '/reports/profitability/shipping-costs',
          title: 'menu.profitability_shipping_costs',
          icon: <></>,
          teamTypes: ['tenant_external'],
          permissions: ['logistics-report.view'],
          requireProfitabilityAccess: true,
        },
      ],
    },
    {
      id: 'merchandise',
      label: 'menu.merchandise',
      path: '/merchandise',
      icon: <FontAwesomeIcon icon={faBoxOpen} className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
      permissions: ['merchandises.view'],
    },
    // 📄 Facturas para tenant_external
    {
      id: 'facturas-external',
      label: 'menu.facturacion_facturas',
      path: '/invoices',
      icon: <Icons.SuscripcionIcon className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
      permissions: ['invoices.view'],
    },
    {
      id: 'billeteras',
      label: 'menu.billeteras',
      path: '/wallets',
      icon: <FontAwesomeIcon icon={faWallet} className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
      permissions: ['wallets.view'],
      requireWalletVisible: true,
    },
    // 🎯 Centro de ayuda → Atención al Cliente (tickets Zendesk in-app)
    {
      id: 'centro-ayuda',
      label: 'menu.centro_ayuda',
      path: '/integrations/zendesk/tickets',
      icon: <FontAwesomeIcon icon={faCircleQuestion} className="!w-4 !h-4" />,
      teamTypes: ['tenant_external'],
    },
  ];
}

function menuSection(
  id: string,
  label: string,
  children: MenuItem[],
  options: Omit<MenuItem, 'id' | 'label' | 'children' | 'isMenuSection'> = {},
): MenuItem {
  return {
    id,
    label,
    isMenuSection: true,
    children,
    ...options,
  };
}

export function getConfigMenuItems(): MenuItem[] {
  return [
    menuSection('config-section-account', 'menu.configSectionAccount', [
      {
        id: 'notificaciones',
        label: 'menu.notificaciones',
        path: '/configuration/notifications',
        icon: <Bell className="!w-4 !h-4" />,
        teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
      },
      {
        id: 'mi-perfil',
        label: 'menu.mi-perfil',
        path: '/configuration/profile',
        icon: <FontAwesomeIcon icon={faUser} className="!w-4 !h-4" />,
        teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
      },
    ]),
    menuSection('config-section-team', 'menu.configSectionTeam', [
      {
        id: 'roles',
        label: 'menu.roles',
        path: '/configuration/roles',
        icon: <FontAwesomeIcon icon={faShield} className="!w-4 !h-4" />,
        teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
        requireRoleAdmin: true,
      },
      {
        id: 'users',
        label: 'menu.users',
        path: '/configuration/users',
        icon: <FontAwesomeIcon icon={faUsers} className="!w-4 !h-4" />,
        teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
        requireRoleAdmin: true,
        roleAdminAlternativeAccess: { teamTypes: ['tenant_3pl'], permissions: ['iam.users.manage-operator-security'] },
      },
      {
        id: 'webhooks',
        label: 'menu.webhooks',
        path: '/configuration/webhooks',
        icon: <Webhook className="!w-4 !h-4" />,
        teamTypes: ['tenant_external'],
        permissions: ['webhooks.view'],
      },
      {
        id: 'integrations',
        label: 'menu.integrations',
        path: '/configuration/integrations',
        icon: <Plug className="!w-4 !h-4" />,
        teamTypes: ['tenant_external'],
        permissions: ['integrations.view'],
      },
    ]),
    menuSection('config-section-admin', 'menu.configSectionAdmin', [
      {
        id: 'impresoras',
        label: 'menu.impresoras',
        path: '/configuration/printers',
        icon: <FontAwesomeIcon icon={faPrint} className="!w-4 !h-4" />,
        teamTypes: ['tenant', 'tenant_3pl'],
        permissions: ['printers.view'],
      },
      {
        id: 'preparation-calendar',
        label: 'menu.preparationCalendar',
        path: '/configuration/preparation-calendar',
        icon: <FontAwesomeIcon icon={faCalendarDays} className="!w-4 !h-4" />,
        teamTypes: ['tenant_3pl'],
        permissions: ['configuration.view'],
      },
      {
        id: 'mensajerias',
        label: 'menu.mensajerias',
        path: '/configuration/couriers',
        icon: <FontAwesomeIcon icon={faBoxOpen} className="!w-4 !h-4" />,
        teamTypes: ['tenant', 'tenant_3pl'],
        permissions: ['shipping.couriers.view'],
      },
      {
        id: 'shipping-rule-presets',
        label: 'menu.shippingRulePresets',
        path: '/configuration/shipping-rule-presets',
        icon: <FontAwesomeIcon icon={faTruck} className="!w-4 !h-4" />,
        teamTypes: ['tenant_3pl'],
        permissions: ['shipping.rules.view'],
      },
      {
        id: 'coverage-zones',
        label: 'menu.coverageZones',
        path: '/configuration/coverage-zones',
        icon: <FontAwesomeIcon icon={faMapLocationDot} className="!w-4 !h-4" />,
        teamTypes: ['tenant', 'tenant_3pl'],
        permissions: ['shipping.coverage.view'],
      },
      {
        id: 'shipment-status-mapping',
        label: 'menu.shipmentStatusMapping',
        path: '/configuration/shipment-status-mapping',
        icon: <FontAwesomeIcon icon={faArrowsRotate} className="!w-4 !h-4" />,
        teamTypes: ['tenant_3pl'],
        permissions: ['shipment_status_mappings.view'],
      },
      {
        id: 'incident-mappings',
        label: 'menu.mapeos',
        path: '/configuration/incident-mappings',
        icon: <FontAwesomeIcon icon={faArrowsRotate} className="!w-4 !h-4" />,
        teamTypes: ['tenant_3pl'],
        permissions: ['incidents.mapping'],
      },
      {
        id: 'popups',
        label: 'menu.gestion_popups',
        path: '/configuration/popups',
        icon: <FontAwesomeIcon icon={faWindowMaximize} className="!w-4 !h-4" />,
        teamTypes: ['tenant_3pl'],
        permissions: ['popups.view'],
      },
      {
        id: 'tracking-monitoring',
        label: 'menu.trackingMonitoring',
        path: '/configuration/tracking-monitoring',
        icon: <FontAwesomeIcon icon={faChartLine} className="!w-4 !h-4" />,
        teamTypes: ['tenant_3pl'],
        permissions: ['tracking-monitoring.view'],
      },
      {
        id: 'tracking-files',
        label: 'menu.trackingFiles',
        path: '/configuration/tracking-files',
        icon: <FontAwesomeIcon icon={faFile} className="!w-4 !h-4" />,
        teamTypes: ['tenant_3pl'],
        permissions: ['tracking-monitoring.view'],
      },
      {
        id: 'product-categories',
        label: 'menu.productCategories',
        path: '/configuration/product-categories',
        icon: <Tags className="!w-4 !h-4" />,
        teamTypes: ['tenant_3pl'],
        permissions: ['categories.view'],
      },
      {
        id: 'warehouse-materials',
        label: 'menu.warehouseMaterials',
        path: '/configuration/warehouse-materials',
        icon: <Box className="!w-4 !h-4" />,
        teamTypes: ['tenant_3pl'],
        permissions: ['materials.manage'],
      },
      {
        id: 'suscripcion',
        label: 'menu.suscripcion',
        path: '/configuration/suscription',
        icon: <Icons.SuscripcionIcon className="!w-4 !h-4" />,
        teamTypes: ['tenant'],
        permissions: ['configuration.view'],
      },
      // Hotfix: afiliados ocultos (ver imports lazy de protectedRoutes).
      // {
      //   id: 'afiliados',
      //   label: 'menu.afiliados',
      //   path: '/configuration/affiliates',
      //   icon: <FontAwesomeIcon icon={faHandHoldingUsd} className="!w-4 !h-4" />,
      //   teamTypes: ['tenant_external'],
      //   permissions: ['affiliates.me.view'],
      // },
    ]),
  ];
}

// 🔚 Items al final del menú principal scrolleable
export function getMainMenuTailItems(): MenuItem[] {
  return [];
}

// 📌 Items fijos en el footer del sidebar, encima del usuario
export function getSidebarFooterItems(onConfigClick: () => void): MenuItem[] {
  return [
    {
      id: 'configuracion',
      label: 'menu.configuracion',
      path: '/' /* se pone '/' para que el icono no quede en opacity */,
      icon: <FontAwesomeIcon icon={faGear} className="!w-4 !h-4" />,
      onClick: onConfigClick,
    },
  ];
}

/** Añade items extra al final del menú principal scrolleable */
export function mergeMainMenuWithTailItems(mainItems: MenuItem[], tailItems: MenuItem[]): MenuItem[] {
  return [...mainItems, ...tailItems];
}

/** @deprecated Usar getSidebarFooterItems */
export function getFooterItems(onConfigClick: () => void): MenuItem[] {
  return getSidebarFooterItems(onConfigClick);
}

// 🔙 Botón de volver en menú de configuración
export function getBackButton(onBackClick: () => void, t: (key: string) => string): MenuItem {
  return {
    id: 'volver',
    label: t('configuracion'),
    icon: <FontAwesomeIcon icon={faChevronLeft} className="!w-4 !h-4" />,
    path: '#',
    onClick: onBackClick,
    isConfigBackButton: true,
  };
}

// 🎨 Items del dropdown del logo
export function getLogoDropdownItems(
  onThemeChange: (theme: 'light' | 'dark' | 'system') => void,
  onLanguageChange: (language: 'es' | 'en') => void,
  t: (key: string) => string,
  teams: Team[] = [], // dYZ_ Recibir usuarios como parametro
  onTeamSelect?: (teamId: number) => void, // ✅ Callback para cambiar de team
  currentTeamId: number | null = null, // ✅ ID del team actual para marcarlo
  iconColorClass: string = '!text-icons', // ✅ Clase de color para iconos
  iconColorStyle: React.CSSProperties = { color: 'var(--wrh-icons)' }, // ✅ Style de color para iconos
): DropdownItem[] {
  const themeItems = [
    {
      onClick: () => onThemeChange('light'),
      label: (
        <div className="flex items-center justify-between text-sm w-full">
          <button className="text-xs transition-colors">
            <span className="flex items-center gap-2">
              <span className={iconColorClass} style={iconColorStyle}>
                <FontAwesomeIcon icon={faSun} className="!w-4 !h-4" />
              </span>
              <span className="text-regular-small leading-normal">{t('themes.light')}</span>
            </span>
          </button>
        </div>
      ),
    },
    {
      onClick: () => onThemeChange('dark'),
      label: (
        <div className="flex items-center justify-between text-sm w-full">
          <button className="text-xs transition-colors">
            <span className="flex items-center gap-2">
              <span className={iconColorClass} style={iconColorStyle}>
                <FontAwesomeIcon icon={faMoon} className="!w-4 !h-4" />
              </span>
              <span className="text-regular-small leading-normal">{t('themes.dark')}</span>
            </span>
          </button>
        </div>
      ),
    },
  ];

  const languageItems = [
    {
      onClick: () => onLanguageChange('es'),
      label: (
        <div className="flex items-center justify-between text-sm w-full">
          <button className="text-xs transition-colors">
            <span className="flex items-center gap-2">
              <span className="text-base">🇪🇸</span>
              <span className="text-regular-small leading-normal">{t('languages.es')}</span>
            </span>
          </button>
        </div>
      ),
    },
    {
      onClick: () => onLanguageChange('en'),
      label: (
        <div className="flex items-center justify-between text-sm w-full">
          <button className="text-xs transition-colors">
            <span className="flex items-center gap-2">
              <span className="text-base">🇬🇧</span>
              <span className="text-regular-small leading-normal">{t('languages.en')}</span>
            </span>
          </button>
        </div>
      ),
    },
  ];

  // dYZ_ Grupo 1: Empresa (usa teams disponibles)
  const empresaChildren: DropdownItem[] =
    teams.length > 0
      ? teams.map((team) => {
          const isCurrentTeam = currentTeamId !== null && team.id === currentTeamId;
          return {
            label: (
              <div className="flex items-center justify-between w-full gap-2">
                <span className="flex-1">{team.name}</span>
                {isCurrentTeam && (
                  <FontAwesomeIcon icon={faCircleCheck} className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </div>
            ),
            onClick: onTeamSelect && !isCurrentTeam ? () => onTeamSelect(team.id) : undefined,
            disabled: !team.id || !onTeamSelect,
          };
        })
      : [];

  const dropdownItems: DropdownItem[] = [
    {
      label: 'menu.empresa',
      icon: (
        <span className={iconColorClass} style={iconColorStyle}>
          <Icons.EmpresaIcon className="!w-4 !h-4" />
        </span>
      ),
      path: undefined, // Sin path directo cuando no hay submenú (general eliminado del menú)
      onClick: teams.length === 0 ? () => console.log('Empresa') : undefined,
      children: empresaChildren.length > 0 ? empresaChildren : undefined,
      childrenSearchable: false,
      teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'], // 🎯 Incluir tenant_external para que usuarios external puedan ver el switch de empresa
    },
  ];

  // 🎯 Separador después del grupo 1
  dropdownItems.push({ type: 'separator', id: 'separator-1' });

  // 🎯 Grupo 2: Perfil y Suscripción
  dropdownItems.push({
    label: 'menu.perfil',
    icon: (
      <span className={iconColorClass} style={iconColorStyle}>
        <Icons.PerfilIcon className="!w-4 !h-4" />
      </span>
    ),
    path: '/configuration/profile',
    onClick: () => console.log('Perfil'),
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'], // 🎯 Todos los tipos pueden ver perfil
  });
  dropdownItems.push({
    label: 'menu.notificaciones',
    icon: (
      <span className={iconColorClass} style={iconColorStyle}>
        <Bell className="!w-4 !h-4" />
      </span>
    ),
    path: '/configuration/notifications',
    onClick: () => console.log('Notificaciones'),
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  });
  dropdownItems.push({
    label: 'menu.suscripcion',
    icon: (
      <span className={iconColorClass} style={iconColorStyle}>
        <Icons.SuscripcionIcon className="!w-4 !h-4" />
      </span>
    ),
    path: '/configuration/suscription',
    onClick: () => console.log('Suscripción'),
    teamTypes: ['tenant'], // 🎯 Solo tenant puede ver suscripción
    permissions: ['global.view-personal-data'], // 🎯 Requiere permiso
  });

  // 🎯 Separador después del grupo 2
  dropdownItems.push({ type: 'separator', id: 'separator-2' });

  // 🎯 Grupo 3: Tema e Idioma
  // ✅ Tema
  dropdownItems.push({
    label: 'menu.theme',
    icon: (
      <span className={iconColorClass} style={iconColorStyle}>
        <FontAwesomeIcon icon={faMoonOverSun} className="!w-4 !h-4" />
      </span>
    ),
    children: themeItems,
    childrenSearchable: false,
  });
  // ✅ Idioma
  dropdownItems.push({
    label: 'menu.language',
    icon: (
      <span className={iconColorClass} style={iconColorStyle}>
        <FontAwesomeIcon icon={faLanguage} className="!w-4 !h-4" />
      </span>
    ),
    children: languageItems,
    childrenSearchable: false,
  });

  // 🎯 Separador después del grupo 3
  dropdownItems.push({ type: 'separator', id: 'separator-3' });

  // 🎯 Grupo 4: Cerrar sesión (solo)
  dropdownItems.push({
    label: <LogoutMenuItem />,
  });

  return dropdownItems;
}
